import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { coursesApi, companiesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, MagnifyingGlass, Pencil, BookOpen, Chalkboard, GearSix, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import BulkDeleteConfirmDialog from '../../components/BulkDeleteConfirmDialog';
import { Checkbox } from '../../components/ui/checkbox';

const TRAINING_TYPES = [
  'Certificación',
  'Calificación',
  'Legal',
  'Seguridad y Salud Ocupacional Específica de Área',
  'Seguridad y Salud Ocupacional General de la Empresa'
];

const MODALITIES = [
  'Presencial',
  'E-Learning Sincrónico',
  'E-Learning Asincrónico'
];

const METHODOLOGIES = [
  'Teórico-Práctico',
  'Teórico',
  'Práctico'
];

export default function CoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [deletingCourse, setDeletingCourse] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    training_type: '',
    default_duration_minutes: 60,
    default_passing_percentage: 70,
    validity_years: '',
    modality: '',
    methodology: '',
    client_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, companiesRes] = await Promise.all([
        coursesApi.getAll(),
        companiesApi.getAll({ is_client: true, page_size: 999 })
      ]);
      setCourses(coursesRes.data);
      const allComps = companiesRes.data.items || companiesRes.data;
      setClients(allComps.filter(c => c.is_client));
    } catch (error) {
      toast.error('Error al cargar cursos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        validity_years: formData.validity_years ? parseInt(formData.validity_years) : null
      };
      if (editingCourse) {
        await coursesApi.update(editingCourse.id, payload);
        toast.success('Curso actualizado');
      } else {
        await coursesApi.create(payload);
        toast.success('Curso creado');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      training_type: '',
      default_duration_minutes: 60,
      default_passing_percentage: 70,
      validity_years: '',
      modality: '',
      methodology: '',
      client_id: ''
    });
    setEditingCourse(null);
  };

  const openEditDialog = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      description: course.description || '',
      training_type: course.training_type,
      default_duration_minutes: course.default_duration_minutes,
      default_passing_percentage: course.default_passing_percentage,
      validity_years: course.validity_years || '',
      modality: course.modality || '',
      methodology: course.methodology || '',
      client_id: course.client_id || ''
    });
    setIsDialogOpen(true);
  };

  const filteredCourses = courses.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || c.training_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allSelected = filteredCourses.every(c => selectedIds.has(c.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      filteredCourses.forEach(c => allSelected ? next.delete(c.id) : next.add(c.id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="courses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Cursos</h1>
          <p className="text-[#2B4B8C]/60">Gestiona los cursos base para capacitaciones</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="create-course-btn">
              <Plus className="mr-2" size={20} />
              Nuevo Curso
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#2B4B8C]">
                {editingCourse ? 'Editar Curso' : 'Nuevo Curso'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Tipo</Label>
                <Select value={formData.training_type} onValueChange={(v) => setFormData({ ...formData, training_type: v })}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#2B4B8C]/10">
                    {TRAINING_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Modalidad</Label>
                  <Select value={formData.modality} onValueChange={(v) => setFormData({ ...formData, modality: v })}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="select-modality">
                      <SelectValue placeholder="Seleccionar modalidad" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      {MODALITIES.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Metodología</Label>
                  <Select value={formData.methodology} onValueChange={(v) => setFormData({ ...formData, methodology: v })}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="select-methodology">
                      <SelectValue placeholder="Seleccionar metodología" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      {METHODOLOGIES.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Duración (min)</Label>
                  <Input
                    type="number"
                    value={formData.default_duration_minutes}
                    onChange={(e) => setFormData({ ...formData, default_duration_minutes: parseInt(e.target.value) })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">% Aprobación</Label>
                  <Input
                    type="number"
                    value={formData.default_passing_percentage}
                    onChange={(e) => setFormData({ ...formData, default_passing_percentage: parseInt(e.target.value) })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Vigencia (años)</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="Ej: 2"
                  value={formData.validity_years}
                  onChange={(e) => setFormData({ ...formData, validity_years: e.target.value })}
                  className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                  data-testid="course-validity-years"
                />
                <p className="text-xs text-[#2B4B8C]/40">Se calcula desde la fecha de realización de la capacitación</p>
              </div>
              <div className="space-y-2">
                <Label className="text-[#2B4B8C]/80">Mandante</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]">
                    <SelectValue placeholder="Seleccionar mandante" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#2B4B8C]/10">
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 border-[#2B4B8C]/10">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]">
                  {editingCourse ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
              <Input
                placeholder="Buscar curso..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-64 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10">
                <SelectItem value="all">Todos los tipos</SelectItem>
                {TRAINING_TYPES.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Delete Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 animate-fade-in" data-testid="bulk-delete-bar-courses">
          <span className="text-sm font-medium text-red-700">
            {selectedIds.size} curso(s) seleccionado(s)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} className="border-red-200 text-red-600 hover:bg-red-50 h-8" data-testid="clear-selection-courses-btn">
              Deseleccionar
            </Button>
            <Button size="sm" onClick={() => setShowBulkDelete(true)} className="bg-red-500 text-white hover:bg-red-600 h-8" data-testid="bulk-delete-courses-btn">
              <Trash size={14} className="mr-1" /> Eliminar Seleccionados
            </Button>
          </div>
        </div>
      )}

      {/* Courses Table */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                <TableHead className="w-10">
                  <Checkbox
                    checked={filteredCourses.length > 0 && filteredCourses.every(c => selectedIds.has(c.id))}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-courses-checkbox"
                  />
                </TableHead>
                <TableHead className="text-[#2B4B8C]/60">Curso</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Modalidad</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Metodología</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Mandante</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Duración</TableHead>
                <TableHead className="text-[#2B4B8C]/60">% Aprobación</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Vigencia</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Capacitaciones</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCourses.map((course) => (
                <TableRow key={course.id} className={`border-[#2B4B8C]/5 hover:bg-[#f8fafc] ${selectedIds.has(course.id) ? 'bg-red-50/50' : ''}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(course.id)}
                      onCheckedChange={() => toggleSelect(course.id)}
                      data-testid={`select-course-${course.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2BBAA4]/10 flex items-center justify-center">
                        <BookOpen size={20} className="text-[#2BBAA4]" />
                      </div>
                      <div>
                        <p className="text-[#2B4B8C] font-medium">{course.name}</p>
                        {course.description && (
                          <p className="text-xs text-[#2B4B8C]/50 truncate max-w-xs">{course.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-[#2B4B8C]/60 text-xs">{course.modality || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60 text-xs">{course.methodology || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{course.client_name || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{course.default_duration_minutes} min</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{course.default_passing_percentage}%</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{course.validity_years ? `${course.validity_years} año(s)` : '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-[#2B4B8C]/60">
                      <Chalkboard size={16} />
                      <span>{course.trainings_count || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/admin/courses/${course.id}/config`)} className="border-[#2BBAA4]/30 text-[#2BBAA4] hover:bg-[#2BBAA4]/10 text-xs" data-testid={`config-course-${course.id}`}>
                      <GearSix size={14} className="mr-1" /> Configurar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(course)} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C]">
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeletingCourse(course)} className="text-red-400 hover:text-red-500" data-testid={`delete-course-${course.id}`}>
                      <Trash size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredCourses.length === 0 && (
            <div className="text-center py-12 text-[#2B4B8C]/50">
              No se encontraron cursos
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingCourse}
        onOpenChange={(open) => { if (!open) setDeletingCourse(null); }}
        entityName="Curso"
        itemName={deletingCourse?.name || ''}
        fetchDependencies={deletingCourse ? () => coursesApi.getDependencies(deletingCourse.id) : null}
        onConfirmDelete={async () => {
          await coursesApi.delete(deletingCourse.id);
          toast.success('Curso eliminado');
          setDeletingCourse(null);
          fetchData();
        }}
      />

      <BulkDeleteConfirmDialog
        open={showBulkDelete}
        onOpenChange={setShowBulkDelete}
        entityName="curso(s)"
        count={selectedIds.size}
        fetchDependencies={() => coursesApi.bulkDependencies([...selectedIds])}
        onConfirmDelete={async () => {
          await coursesApi.bulkDelete([...selectedIds]);
          toast.success(`${selectedIds.size} curso(s) eliminado(s)`);
          clearSelection();
          fetchData();
        }}
      />
    </div>
  );
}
